<div class="hx-rating__container-host" [class]="hostCssClasses()">
  @if (processedLabels().question) {
    <hx-label
      class="hx-rating__label"
      [description]="processedLabels().question"
      [subtitle]="processedLabels().subtitle"
      [required]="required()"
      [for]="computedId()"
      [id]="'question-' + computedId()"
      [color]="labelColor()"
      [subtitleColor]="subtitleColor()"
      [fontWeight]="labelFontWeight()"
      [fontSize]="labelFontSize()"
      [subtitleFontWeight]="subtitleFontWeight()"
      [subtitleFontSize]="subtitleFontSize()"
      [tooltip]="tooltip()"
      [tooltipPosition]="tooltipPosition()"
      [tooltipColor]="tooltipColor()"></hx-label>
  }

  <div
    class="hx-rating__body"
    role="radiogroup"
    [attr.aria-describedby]="ariaDescribedBy()"
    [attr.aria-disabled]="disabledState()"
    [attr.aria-invalid]="validationError().isInvalid"
    [attr.aria-labelledby]="'question-' + computedId()"
    [attr.aria-required]="required()"
    [attr.id]="computedId()">
    <div class="hx-rating__scale-wrapper">
      @if (showSlider() && type() !== 'star') {
        <div class="hx-rating__slider-container">
          <input
            aria-hidden="true"
            class="hx-rating__slider"
            tabindex="-1"
            type="range"
            [attr.disabled]="disabledState() ? '' : null"
            [disabled]="disabledState()"
            [max]="maxRatingValue()"
            [min]="firstFocusableItem()"
            [step]="1"
            [style.--range-progress]="sliderProgress()"
            [style.pointer-events]="disabledState() ? 'none' : 'auto'"
            [value]="selectedValue() ?? 0"
            (input)="onSliderInput($event)" />
        </div>
      }

      <div class="hx-rating__container" [style.--rating-columns]="columns()">
        @for (colIndex of getColumnIndices(); track colIndex) {
          <div class="hx-rating__column">
            <div class="hx-rating__items">
              @for (item of getItemsForColumn(); track item) {
                <div class="hx-rating__item-wrapper">
                  @if (type() === 'numerical') {
                    <button
                      class="hx-rating__item"
                      hxButton
                      type="button"
                      [attr.aria-checked]="selectedValue() === item"
                      [attr.aria-label]="'Rating ' + item + ' of ' + maxRatingValue()"
                      [attr.tabindex]="
                        selectedValue() === item || (selectedValue() === null && item === firstFocusableItem()) ? 0 : -1
                      "
                      [disabled]="disabledState()"
                      [ngClass]="itemCssClasses().get(item)"
                      [size]="size()"
                      [variant]="isActive(item) ? 'fill' : 'outline'"
                      (blur)="onBlur()"
                      (click)="selectRating(item)"
                      (focus)="onFocus()"
                      (keydown)="onKeyDown($event, item)"
                      (mouseenter)="onItemHover(item)"
                      (mouseleave)="onItemLeave()">
                      <span class="hx-rating__item-text">
                        {{ getRatingItemContent(item) }}
                      </span>
                    </button>
                  }

                  @if (type() === 'star') {
                    <div
                      class="hx-rating__star-container"
                      role="radio"
                      [attr.aria-checked]="selectedValue() === item"
                      [attr.aria-disabled]="disabledState()"
                      [attr.aria-label]="'Rating ' + item + ' of ' + maxRatingValue()"
                      [attr.tabindex]="
                        !disabledState() &&
                        (selectedValue() === item || (selectedValue() === null && item === firstFocusableItem()))
                          ? 0
                          : -1
                      "
                      [class.hx-rating__star-container--active]="isActive(item)"
                      [class.hx-rating__star-container--disabled]="disabledState()"
                      [style.height]="'var(--hx-rating-item-size)'"
                      [style.pointer-events]="disabledState() ? 'none' : 'auto'"
                      [style.width]="'var(--hx-rating-item-size)'"
                      (blur)="onBlur()"
                      (click)="!disabledState() && !readonly() && selectRating(item)"
                      (focus)="onFocus()"
                      (keydown)="onKeyDown($event, item)"
                      (mouseenter)="onItemHover(item)"
                      (mouseleave)="onItemLeave()">
                      <hx-icon
                        class="hx-rating__visual-icon"
                        [filled]="isActive(item)"
                        [inline]="true"
                        [svgIcon]="ICON" />
                    </div>
                  }

                  @if (getItemLabel(item)) {
                    <div class="hx-rating__item-label">
                      {{ getItemLabel(item) }}
                    </div>
                  }

                  @if (enableCodeMapping() && getItemCodes(item).length > 0) {
                    <div class="hx-rating__item-badges">
                      @for (codeItem of getItemCodes(item); track codeItem.code) {
                        <hx-tag
                          [label]="codeItem.code || codeItem.display || ''"
                          [attr.title]="codeItem.display || codeItem.description || codeItem.code"
                          [removable]="!disabledState() && !readonly()"
                          variant="filled"
                          color="primary"
                          (removed)="onRemoveCode(item, codeItem.code)">
                          @if (getCodeType(codeItem)) {
                            <span class="hx-rating__tag-type">{{ getCodeType(codeItem) }}</span>
                          }
                        </hx-tag>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>

      @if (processedLabels().min || processedLabels().max) {
        <div aria-hidden="true" class="hx-rating__endpoint-labels">
          <span class="hx-rating__min-label">{{ processedLabels().min }}</span>
          <span class="hx-rating__max-label">{{ processedLabels().max }}</span>
        </div>
      }
    </div>
  </div>

  @if (validationError().isInvalid) {
    <div class="hx-rating__error" role="alert" [id]="'error-' + computedId()">
      {{ validationError().message }}
    </div>
  } @else if (processedLabels().help) {
    <div class="hx-rating__hint" [id]="'help-' + computedId()">
      {{ processedLabels().help }}
    </div>
  }
</div>
